// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Two screens, because there are two jobs and they are not the same job:
//   /my-attendance  one worker, one decision, one button — used every day
//   /attendance     the supervisor's review of everybody — used once a day
// Splitting them by route rather than branching inside one page means each is
// gated by its own permission and neither has to know the other exists.

export const routes = [
  {
    path: '/my-attendance',
    name: 'my-attendance',
    component: () => import('./pages/MyAttendancePage.vue'),
    meta: { permission: 'attendance:clock', title: 'attendance.my.title' },
  },
  {
    path: '/attendance',
    name: 'attendance',
    component: () => import('./pages/AttendancePage.vue'),
    meta: { permission: 'attendance:read', title: 'attendance.title' },
  },
]

// Both entries in `business`, including the worker's own screen: the sidebar is
// split by who owns the screen, and `main` belongs to the shell. A module that
// puts its most-used page up there buys visibility for itself and takes it from
// the next module installed, which has the same claim to it.
export const navigation = [
  // Fichar es lo primero y lo último que se hace en el día, y las dos veces se
  // hace de pie. Va abajo en un teléfono; ver `fieldNavigation` en el armazón.
  { to: '/my-attendance', labelKey: 'attendance.my.title', permission: 'attendance:clock', icon: 'clock', group: 'business', order: 30 },
  { to: '/attendance', labelKey: 'attendance.title', permission: 'attendance:read', icon: 'calendar', group: 'business' },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (section 16): a module that ships two is a module that cannot be
// sold to the third client.
const baseMessages = {
  es: {
    attendance: {
      // Las respuestas que contestan las reglas sin el modelo (intents.js).
      assistant: {
        query: {
          status: {
            in: 'Estás fichado desde las {since}; llevas {hours} h {minutes} min hoy.',
            paused: 'Estás en pausa; llevas {hours} h {minutes} min hoy.',
            done: 'Fichaste la salida a las {until}; hoy hiciste {hours} h {minutes} min.',
            out: 'Hoy todavía no has fichado.',
          },
          hours: {
            today: 'Hoy llevas {hours} h {minutes} min.',
            week: 'Esta semana llevas {hours} h {minutes} min, en {days} días.',
            month: 'Este mes llevas {hours} h {minutes} min, en {days} días.',
          },
        },
      },
      title: 'Fichajes',
      dashboard: {
        myDay: 'Mi jornada',
        myDayHelp: 'Si estás dentro o fuera, lo que llevas hoy, y el botón para entrar, pausar o salir.',
        workingNow: 'Trabajando ahora',
        workingNowHelp: 'Cuántas personas tienen la jornada abierta en este momento, pausas incluidas.',
        onBreak: 'En pausa',
        onBreakHelp: 'De las que están trabajando, cuántas tienen una pausa abierta ahora mismo.',
        who: 'Quién está trabajando',
        whoHelp: 'Quién tiene la jornada abierta, con lo que lleva trabajado hoy. Estira la tarjeta y caben más.',
        break: 'En pausa',
      },
      subtitle: 'Registro de jornada del equipo',
      inProgress: 'En curso',
      unknownWorker: 'Sin asignar',
      exportDone: 'Exportado. Sello {hash}',
      actions: {
        registerDay: 'Registrar jornada', export: 'Exportar', refresh: 'Actualizar',
        close: 'Cerrar', approve: 'Aprobar', view: 'Ver', void: 'Anular',
        correct: 'Corregir', cancel: 'Cancelar',
        clock_in: 'Entrar', clock_out: 'Salir', pause_start: 'Pausa', pause_end: 'Reanudar',
      },
      filters: { title: 'Filtros', from: 'Desde', to: 'Hasta', worker: 'Trabajador', allWorkers: 'Todos' },
      kpi: { title: 'Resumen', workers: 'Trabajadores', approved: 'Aprobadas', pending: 'Pendientes', open: 'Sin cerrar', warnings: 'Con avisos' },
      tabs: { days: 'Jornadas', entries: 'Fichajes' },
      table: {
        rules: 'Convenio',
        worker: 'Trabajador', date: 'Fecha', in: 'Entrada', out: 'Salida',
        worked: 'Efectivas', break: 'Pausa', entries: 'Nº', validation: 'Validación',
        type: 'Tipo', time: 'Momento', origin: 'Origen', byAdmin: 'Administrativo', imported: 'Importado',
      },
      empty: {
        days: 'No hay jornadas en este periodo',
        daysHint: 'Prueba a ampliar el rango de fechas o a quitar el filtro de trabajador.',
        entries: 'No hay fichajes en este periodo',
        entriesHint: 'Aquí aparece cada entrada, pausa y salida, una por línea.',
      },
      status: { pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada' },
      types: { clock_in: 'Entrada', clock_out: 'Salida', pause_start: 'Inicio pausa', pause_end: 'Fin pausa' },
      modality: { on_site: 'Presencial', remote: 'Teletrabajo' },
      hourKind: { ordinary: 'Ordinarias', overtime: 'Extraordinarias', complementary: 'Complementarias' },
      state: { idle: 'Sin fichar', working: 'Trabajando', paused: 'En pausa', done: 'Jornada cerrada' },
      done: { clock_in: 'Jornada iniciada', clock_out: 'Jornada cerrada', pause_start: 'Pausa iniciada', pause_end: 'Pausa terminada' },
      fields: {
        reason: 'Motivo', reasonPlaceholder: 'Por qué se registra esto',
        worker: 'Trabajador', workerPlaceholder: 'Busca por nombre',
        date: 'Fecha', clockIn: 'Entrada', clockOut: 'Salida',
        pauseStart: 'Inicio pausa', pauseEnd: 'Fin pausa',
        modality: 'Modalidad', hourKind: 'Tipo de horas', notes: 'Notas',
        type: 'Tipo', time: 'Momento',
      },
      reasons: {
        forgot: 'Olvidó fichar', deviceFailure: 'Fallo del dispositivo',
        appFailure: 'Fallo de la aplicación', noDevice: 'Sin dispositivo disponible',
        confirmedBySupervisor: 'Confirmado por el encargado', confirmedByWorker: 'Confirmado por el trabajador',
      },
      errors: { workerRequired: 'Selecciona un trabajador', reasonRequired: 'El motivo es obligatorio' },
      registerDay: {
        title: 'Registrar jornada',
        hint: 'Para cuando alguien trabajó sin poder fichar. Queda marcada como administrativa con tu motivo.',
        clockOutHint: 'Déjalo vacío si la jornada sigue abierta',
        withBreak: 'Con pausa', submit: 'Registrar', done: 'Jornada registrada ({count} fichajes)',
      },
      closeDay: {
        title: 'Cerrar jornada',
        hint: 'Jornada de {worker} del {date}, sin hora de salida.',
        startedAt: 'Entró a las {time}', submit: 'Cerrar jornada', done: 'Jornada cerrada',
      },
      validate: {
        title: 'Aprobar jornada',
        hint: 'Jornada de {worker} del {date}. Aprobar equivale a tu firma sobre estas horas.',
        changedFrom: 'antes {time}', correctionReason: 'Motivo de la corrección',
        notesHint: 'Opcional, queda en el registro',
        submit: 'Aprobar', submitWithChanges: 'Corregir ({count}) y aprobar', done: 'Jornada aprobada',
      },
      correct: {
        title: 'Corregir fichaje',
        hint: 'El original no se borra: queda anulado y este lo sustituye.',
        submit: 'Corregir', done: 'Fichaje corregido',
      },
      void: {
        title: 'Anular jornada',
        hint: 'Vas a anular la jornada de {worker} del {date}.',
        keeps: 'Los fichajes no se borran: quedan marcados como anulados con tu motivo.',
        submit: 'Anular', done: 'Jornada anulada',
      },
      dayEntries: {
        title: 'Detalle de la jornada', hint: '{worker} · {date}',
        byAdmin: 'Registrado por administración: {reason}', corrects: 'Sustituye a un fichaje corregido', imported: 'Importado de {system}', recordedThere: 'anotado allí el {at}',
      },
      my: {
        title: 'Mi jornada', subtitle: 'Tu registro de horas',
        workedToday: 'trabajadas hoy', worked: 'Trabajadas', today: 'Hoy',
        history: 'Historial', week: 'Semana', month: 'Mes', export: 'Descargar mes',
        noHistory: 'Todavía no hay jornadas',
        noHistoryHint: 'En cuanto fiches una entrada, tus jornadas aparecerán aquí.',
        dayDone: 'Has cerrado la jornada de hoy.', reopen: 'Empezar otra jornada',
        notStarted: 'Aún no has fichado hoy',
        startedAt: 'Desde las {time}', pausedAt: 'En pausa desde las {time}', finishedAt: 'Saliste a las {time}',
      },
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Fichajes",
        subtitle: "El registro de jornada que exige la ley, y por qué no cuenta las horas de un trabajo",
      },
      rules: {
        title: 'Convenio',
        agreement: 'Convenio: {name}',
        none: 'Sin avisos del convenio',
        counted: 'Computables: {time}',
        paidBreak: 'Incluye {time} de pausa pagada por convenio',
        hint: 'Son avisos: el registro no se ha tocado y el fichaje no se ha impedido.',
        unknown: 'Aviso de una regla del cliente ({code})',
        warnings: {
          max_ordinary_hours: 'Jornada de {worked}: pasa de las {limit} ordinarias',
          min_rest: 'Descanso de {rest} desde la jornada anterior; el mínimo es {required}',
          break_missing: '{continuous} seguidas sin una pausa de al menos {minBreak}',
        },
      },
      search: { subtitle: 'Ver sus jornadas' },
      notifications: {
        categories: { dayLeftOpen: 'Jornadas sin cerrar', dayRules: 'Avisos del convenio' },
        dayRules: { title: 'Hay jornadas que no cumplen el convenio' },
        dayLeftOpen: { title: 'Hay jornadas sin cerrar' },
        yourDayLeftOpen: { title: 'Tu jornada sigue abierta', message: 'No has fichado la salida de hoy.' },
      },
    },

    // ── Lo que este módulo se llama en las pantallas de la plataforma ───────
    // Roles, ajustes y auditoría son de la plataforma, pero los textos que
    // hablan de fichajes son de este módulo. Van aquí para que instalarlo deje
    // esas tres pantallas completas sin tocar la plataforma.
    modules: { attendance: 'Fichajes' },
    // Las aperturas que este módulo aporta al panel del asistente. La clave es
    // el id estable registrado en backend/assistant.js; el texto inglés de allí
    // es solo el fallback.
    assistant: {
      openers: {
        attendance: {
          'am-i-clocked-in': '¿Estoy fichado ahora mismo?',
          'my-hours-this-week': '¿Cuántas horas llevo esta semana?',
          'who-is-working-now': '¿Quién está trabajando ahora?',
        },
      },
    },
    permissions: {
      attendance: {
        clock: 'Fichar y ver lo propio',
        read: 'Ver las jornadas de todos',
        manage: 'Corregir, validar y anular jornadas',
        export: 'Exportar el registro de jornada',
        import: 'Importar el registro de jornada de otro sistema',
      },
    },
    settings: {
      ns: {
        attendance: {
          title: 'Fichajes',
          subtitle: 'Conservación del registro, avisos y reglas del convenio',
          retentionYears: {
            label: 'Años de conservación del registro',
            help: 'La ley exige cuatro. Lo anterior se anonimiza, nunca se borra.',
          },
          openDayAlertRoles: {
            label: 'Roles avisados de jornadas sin cerrar',
            help: 'Reciben un aviso cada noche con la lista.',
          },
          defaultCloseTime: { label: 'Hora de salida por defecto al cerrar una jornada a mano' },
          groups: { record: 'Registro', alerts: 'Avisos', agreement: 'Convenio' },
          agreementName: {
            label: 'Convenio colectivo',
            help: 'El convenio del que salen estas reglas, para que quien lea un aviso sepa qué texto consultar.',
          },
          agreementNotes: {
            label: 'Notas del convenio',
            help: 'Lo que las reglas de abajo no pueden expresar. Se enseña junto a los avisos.',
          },
          maxOrdinaryDailyHours: {
            label: 'Máximo de horas ordinarias al día',
            help: 'El art. 34.3 ET fija nueve salvo que el convenio diga otra cosa. Las jornadas apuntadas como extra no avisan. 0 lo desactiva.',
          },
          minRestHours: {
            label: 'Descanso mínimo entre jornadas',
            help: 'Art. 34.3 ET: doce horas desde que acaba una jornada hasta que empieza la siguiente. 0 lo desactiva.',
          },
          breakAfterHours: {
            label: 'Horas seguidas antes de la pausa obligatoria',
            help: 'Art. 34.4 ET: pausa cuando la jornada continuada pasa de seis horas. 0 lo desactiva.',
          },
          minBreakMinutes: {
            label: 'Pausa mínima',
            help: 'Art. 34.4 ET: al menos quince minutos. Una parada más corta no cuenta como la pausa.',
          },
          toleranceMinutes: {
            label: 'Tolerancia de fichaje',
            help: 'Margen antes de que avise cualquiera de estas reglas: con 5, nueve horas y cuatro minutos no pasa de nueve.',
          },
          paidBreakMinutes: {
            label: 'Pausa que computa como trabajo',
            help: 'Minutos de pausa al día que paga el convenio, como el bocadillo. Se enseñan como horas computables; las trabajadas no cambian.',
          },
          ruleAlertRoles: {
            label: 'Roles avisados de incumplimientos del convenio',
            help: 'Vacío: los avisos solo se ven en pantalla. Con roles: un aviso cada noche con las jornadas que no cumplen.',
          },
        },
      },
    },
    audit: {
      actions: {
        attendance: {
          registered_for_worker: 'Fichaje registrado en nombre de un trabajador',
          day_registered_for_worker: 'Jornada registrada en nombre de un trabajador',
          corrected: 'Fichaje corregido',
          day_validated: 'Jornada aprobada',
          day_voided: 'Jornada anulada',
          exported: 'Registro de jornada exportado',
          imported: 'Fichaje importado de otro sistema',
        },
      },
    },
  },

  ca: {
    attendance: {
      // Las respuestas que contestan las reglas sin el modelo (intents.js).
      assistant: {
        query: {
          status: {
            in: 'Estàs fitxat des de les {since}; portes {hours} h {minutes} min avui.',
            paused: 'Estàs en pausa; portes {hours} h {minutes} min avui.',
            done: 'Vas fitxar la sortida a les {until}; avui has fet {hours} h {minutes} min.',
            out: 'Avui encara no has fitxat.',
          },
          hours: {
            today: 'Avui portes {hours} h {minutes} min.',
            week: 'Aquesta setmana portes {hours} h {minutes} min, en {days} dies.',
            month: 'Aquest mes portes {hours} h {minutes} min, en {days} dies.',
          },
        },
      },
      title: 'Fitxatges',
      dashboard: {
        myDay: 'La meva jornada',
        myDayHelp: 'Si ets dins o fora, el que portes avui, i el botó per entrar, pausar o sortir.',
        workingNow: 'Treballant ara',
        workingNowHelp: 'Quantes persones tenen la jornada oberta en aquest moment, pauses incloses.',
        onBreak: 'En pausa',
        onBreakHelp: 'De les que estan treballant, quantes tenen una pausa oberta ara mateix.',
        who: 'Qui està treballant',
        whoHelp: 'Qui té la jornada oberta, amb el que porta treballat avui. Estira la targeta i n\'hi caben més.',
        break: 'En pausa',
      },
      subtitle: "Registre de jornada de l'equip",
      inProgress: 'En curs',
      unknownWorker: 'Sense assignar',
      exportDone: 'Exportat. Segell {hash}',
      actions: {
        registerDay: 'Registrar jornada', export: 'Exportar', refresh: 'Actualitzar',
        close: 'Tancar', approve: 'Aprovar', view: 'Veure', void: 'Anul·lar',
        correct: 'Corregir', cancel: 'Cancel·lar',
        clock_in: 'Entrar', clock_out: 'Sortir', pause_start: 'Pausa', pause_end: 'Reprendre',
      },
      filters: { title: 'Filtres', from: 'Des de', to: 'Fins a', worker: 'Treballador', allWorkers: 'Tots' },
      kpi: { title: 'Resum', workers: 'Treballadors', approved: 'Aprovades', pending: 'Pendents', open: 'Sense tancar', warnings: 'Amb avisos' },
      tabs: { days: 'Jornades', entries: 'Fitxatges' },
      table: {
        rules: 'Conveni',
        worker: 'Treballador', date: 'Data', in: 'Entrada', out: 'Sortida',
        worked: 'Efectives', break: 'Pausa', entries: 'Núm.', validation: 'Validació',
        type: 'Tipus', time: 'Moment', origin: 'Origen', byAdmin: 'Administratiu', imported: 'Importat',
      },
      empty: {
        days: 'No hi ha jornades en aquest període',
        daysHint: "Prova d'ampliar el rang de dates o de treure el filtre de treballador.",
        entries: 'No hi ha fitxatges en aquest període',
        entriesHint: 'Aquí apareix cada entrada, pausa i sortida, una per línia.',
      },
      status: { pending: 'Pendent', approved: 'Aprovada', rejected: 'Rebutjada' },
      types: { clock_in: 'Entrada', clock_out: 'Sortida', pause_start: 'Inici pausa', pause_end: 'Fi pausa' },
      modality: { on_site: 'Presencial', remote: 'Teletreball' },
      hourKind: { ordinary: 'Ordinàries', overtime: 'Extraordinàries', complementary: 'Complementàries' },
      state: { idle: 'Sense fitxar', working: 'Treballant', paused: 'En pausa', done: 'Jornada tancada' },
      done: { clock_in: 'Jornada iniciada', clock_out: 'Jornada tancada', pause_start: 'Pausa iniciada', pause_end: 'Pausa acabada' },
      fields: {
        reason: 'Motiu', reasonPlaceholder: 'Per què es registra això',
        worker: 'Treballador', workerPlaceholder: 'Cerca per nom',
        date: 'Data', clockIn: 'Entrada', clockOut: 'Sortida',
        pauseStart: 'Inici pausa', pauseEnd: 'Fi pausa',
        modality: 'Modalitat', hourKind: "Tipus d'hores", notes: 'Notes',
        type: 'Tipus', time: 'Moment',
      },
      reasons: {
        forgot: 'Va oblidar fitxar', deviceFailure: 'Errada del dispositiu',
        appFailure: "Errada de l'aplicació", noDevice: 'Sense dispositiu disponible',
        confirmedBySupervisor: "Confirmat per l'encarregat", confirmedByWorker: 'Confirmat pel treballador',
      },
      errors: { workerRequired: 'Selecciona un treballador', reasonRequired: 'El motiu és obligatori' },
      registerDay: {
        title: 'Registrar jornada',
        hint: 'Per quan algú va treballar sense poder fitxar. Queda marcada com a administrativa amb el teu motiu.',
        clockOutHint: 'Deixa-ho buit si la jornada segueix oberta',
        withBreak: 'Amb pausa', submit: 'Registrar', done: 'Jornada registrada ({count} fitxatges)',
      },
      closeDay: {
        title: 'Tancar jornada',
        hint: 'Jornada de {worker} del {date}, sense hora de sortida.',
        startedAt: 'Va entrar a les {time}', submit: 'Tancar jornada', done: 'Jornada tancada',
      },
      validate: {
        title: 'Aprovar jornada',
        hint: 'Jornada de {worker} del {date}. Aprovar equival a la teva signatura sobre aquestes hores.',
        changedFrom: 'abans {time}', correctionReason: 'Motiu de la correcció',
        notesHint: 'Opcional, queda al registre',
        submit: 'Aprovar', submitWithChanges: 'Corregir ({count}) i aprovar', done: 'Jornada aprovada',
      },
      correct: {
        title: 'Corregir fitxatge',
        hint: "L'original no s'esborra: queda anul·lat i aquest el substitueix.",
        submit: 'Corregir', done: 'Fitxatge corregit',
      },
      void: {
        title: 'Anul·lar jornada',
        hint: 'Vas a anul·lar la jornada de {worker} del {date}.',
        keeps: "Els fitxatges no s'esborren: queden marcats com a anul·lats amb el teu motiu.",
        submit: 'Anul·lar', done: 'Jornada anul·lada',
      },
      dayEntries: {
        title: 'Detall de la jornada', hint: '{worker} · {date}',
        byAdmin: 'Registrat per administració: {reason}', corrects: 'Substitueix un fitxatge corregit', imported: 'Importat de {system}', recordedThere: 'anotat allà el {at}',
      },
      my: {
        title: 'La meva jornada', subtitle: 'El teu registre d\'hores',
        workedToday: 'treballades avui', worked: 'Treballades', today: 'Avui',
        history: 'Historial', week: 'Setmana', month: 'Mes', export: 'Descarregar mes',
        noHistory: 'Encara no hi ha jornades',
        noHistoryHint: 'Quan fitxis una entrada, les teves jornades apareixeran aquí.',
        dayDone: "Has tancat la jornada d'avui.", reopen: 'Començar una altra jornada',
        notStarted: 'Encara no has fitxat avui',
        startedAt: 'Des de les {time}', pausedAt: 'En pausa des de les {time}', finishedAt: 'Vas sortir a les {time}',
      },
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Fitxatges",
        subtitle: "El registre de jornada que exigeix la llei, i per què no compta les hores d’una feina",
      },
      rules: {
        title: 'Conveni',
        agreement: 'Conveni: {name}',
        none: 'Sense avisos del conveni',
        counted: 'Computables: {time}',
        paidBreak: 'Inclou {time} de pausa pagada pel conveni',
        hint: "Són avisos: el registre no s'ha tocat i el fitxatge no s'ha impedit.",
        unknown: "Avís d'una regla del client ({code})",
        warnings: {
          max_ordinary_hours: 'Jornada de {worked}: passa de les {limit} ordinàries',
          min_rest: 'Descans de {rest} des de la jornada anterior; el mínim és {required}',
          break_missing: '{continuous} seguides sense una pausa de com a mínim {minBreak}',
        },
      },
      search: { subtitle: 'Veure les seves jornades' },
      notifications: {
        categories: { dayLeftOpen: 'Jornades sense tancar', dayRules: 'Avisos del conveni' },
        dayRules: { title: 'Hi ha jornades que no compleixen el conveni' },
        dayLeftOpen: { title: 'Hi ha jornades sense tancar' },
        yourDayLeftOpen: { title: 'La teva jornada segueix oberta', message: "No has fitxat la sortida d'avui." },
      },
    },

    modules: { attendance: 'Fitxatges' },
    assistant: {
      openers: {
        attendance: {
          'am-i-clocked-in': 'Estic fitxat ara mateix?',
          'my-hours-this-week': 'Quantes hores porto aquesta setmana?',
          'who-is-working-now': 'Qui està treballant ara?',
        },
      },
    },
    permissions: {
      attendance: {
        clock: 'Fitxar i veure el que és propi',
        read: 'Veure les jornades de tothom',
        manage: 'Corregir, validar i anul·lar jornades',
        export: 'Exportar el registre de jornada',
        import: "Importar el registre de jornada d'un altre sistema",
      },
    },
    settings: {
      ns: {
        attendance: {
          title: 'Fitxatges',
          subtitle: 'Conservació del registre, avisos i regles del conveni',
          retentionYears: {
            label: 'Anys de conservació del registre',
            help: "La llei n'exigeix quatre. El que és anterior s'anonimitza, mai s'esborra.",
          },
          openDayAlertRoles: {
            label: 'Rols avisats de jornades sense tancar',
            help: 'Reben un avís cada nit amb la llista.',
          },
          defaultCloseTime: { label: 'Hora de sortida per defecte en tancar una jornada a mà' },
          groups: { record: 'Registre', alerts: 'Avisos', agreement: 'Conveni' },
          agreementName: {
            label: 'Conveni col·lectiu',
            help: "El conveni d'on surten aquestes regles, perquè qui llegeixi un avís sàpiga quin text consultar.",
          },
          agreementNotes: {
            label: 'Notes del conveni',
            help: "El que les regles de sota no poden expressar. Es mostra al costat dels avisos.",
          },
          maxOrdinaryDailyHours: {
            label: 'Màxim d’hores ordinàries al dia',
            help: "L'art. 34.3 ET en fixa nou llevat que el conveni digui una altra cosa. Les jornades apuntades com a extra no avisen. 0 ho desactiva.",
          },
          minRestHours: {
            label: 'Descans mínim entre jornades',
            help: "Art. 34.3 ET: dotze hores des que acaba una jornada fins que comença la següent. 0 ho desactiva.",
          },
          breakAfterHours: {
            label: 'Hores seguides abans de la pausa obligatòria',
            help: "Art. 34.4 ET: pausa quan la jornada continuada passa de sis hores. 0 ho desactiva.",
          },
          minBreakMinutes: {
            label: 'Pausa mínima',
            help: "Art. 34.4 ET: com a mínim quinze minuts. Una parada més curta no compta com la pausa.",
          },
          toleranceMinutes: {
            label: 'Tolerància de fitxatge',
            help: "Marge abans que avisi qualsevol d'aquestes regles: amb 5, nou hores i quatre minuts no passa de nou.",
          },
          paidBreakMinutes: {
            label: 'Pausa que computa com a treball',
            help: "Minuts de pausa al dia que paga el conveni, com l'esmorzar. Es mostren com a hores computables; les treballades no canvien.",
          },
          ruleAlertRoles: {
            label: 'Rols avisats d’incompliments del conveni',
            help: "Buit: els avisos només es veuen a la pantalla. Amb rols: un avís cada nit amb les jornades que no compleixen.",
          },
        },
      },
    },
    audit: {
      actions: {
        attendance: {
          registered_for_worker: "Fitxatge registrat en nom d'un treballador",
          day_registered_for_worker: "Jornada registrada en nom d'un treballador",
          corrected: 'Fitxatge corregit',
          day_validated: 'Jornada aprovada',
          day_voided: 'Jornada anul·lada',
          exported: 'Registre de jornada exportat',
          imported: "Fitxatge importat d'un altre sistema",
        },
      },
    },
  },

  en: {
    attendance: {
      // Las respuestas que contestan las reglas sin el modelo (intents.js).
      assistant: {
        query: {
          status: {
            in: 'You are clocked in since {since}; {hours} h {minutes} min today.',
            paused: 'You are on a break; {hours} h {minutes} min today.',
            done: 'You clocked out at {until}; {hours} h {minutes} min today.',
            out: 'You have not clocked in today.',
          },
          hours: {
            today: '{hours} h {minutes} min worked today.',
            week: '{hours} h {minutes} min worked this week, over {days} days.',
            month: '{hours} h {minutes} min worked this month, over {days} days.',
          },
        },
      },
      title: 'Attendance',
      dashboard: {
        myDay: 'My working day',
        myDayHelp: 'Whether you are in or out, how long you have worked today, and the button to start, pause or finish.',
        workingNow: 'Working now',
        workingNowHelp: 'How many people have their day open right now, breaks included.',
        onBreak: 'On a break',
        onBreakHelp: 'Of the people working, how many have a break open at this moment.',
        who: 'Who is working',
        whoHelp: 'Who has their day open, and how long each has worked today. Stretch the card and more fit.',
        break: 'On a break',
      },
      subtitle: 'Working-time record for the team',
      inProgress: 'In progress',
      unknownWorker: 'Unassigned',
      exportDone: 'Exported. Seal {hash}',
      actions: {
        registerDay: 'Record a day', export: 'Export', refresh: 'Refresh',
        close: 'Close', approve: 'Approve', view: 'View', void: 'Void',
        correct: 'Correct', cancel: 'Cancel',
        clock_in: 'Clock in', clock_out: 'Clock out', pause_start: 'Break', pause_end: 'Resume',
      },
      filters: { title: 'Filters', from: 'From', to: 'To', worker: 'Worker', allWorkers: 'Everyone' },
      kpi: { title: 'Summary', workers: 'Workers', approved: 'Approved', pending: 'Pending', open: 'Left open', warnings: 'With warnings' },
      tabs: { days: 'Days', entries: 'Entries' },
      table: {
        rules: 'Agreement',
        worker: 'Worker', date: 'Date', in: 'In', out: 'Out',
        worked: 'Worked', break: 'Break', entries: 'No.', validation: 'Validation',
        type: 'Type', time: 'Time', origin: 'Origin', byAdmin: 'Administrative', imported: 'Imported',
      },
      empty: {
        days: 'No days in this period',
        daysHint: 'Try widening the date range or clearing the worker filter.',
        entries: 'No entries in this period',
        entriesHint: 'Every clock in, break and clock out shows up here, one per line.',
      },
      status: { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' },
      types: { clock_in: 'Clock in', clock_out: 'Clock out', pause_start: 'Break start', pause_end: 'Break end' },
      modality: { on_site: 'On site', remote: 'Remote' },
      hourKind: { ordinary: 'Ordinary', overtime: 'Overtime', complementary: 'Complementary' },
      state: { idle: 'Not clocked in', working: 'Working', paused: 'On a break', done: 'Day closed' },
      done: { clock_in: 'Day started', clock_out: 'Day closed', pause_start: 'Break started', pause_end: 'Break ended' },
      fields: {
        reason: 'Reason', reasonPlaceholder: 'Why this is being recorded',
        worker: 'Worker', workerPlaceholder: 'Search by name',
        date: 'Date', clockIn: 'Start', clockOut: 'End',
        pauseStart: 'Break start', pauseEnd: 'Break end',
        modality: 'Modality', hourKind: 'Hour type', notes: 'Notes',
        type: 'Type', time: 'Time',
      },
      reasons: {
        forgot: 'Forgot to clock', deviceFailure: 'Device failure',
        appFailure: 'App failure', noDevice: 'No device available',
        confirmedBySupervisor: 'Confirmed by the supervisor', confirmedByWorker: 'Confirmed by the worker',
      },
      errors: { workerRequired: 'Pick a worker', reasonRequired: 'A reason is required' },
      registerDay: {
        title: 'Record a day',
        hint: 'For when somebody worked with no way to clock. It is marked administrative and carries your reason.',
        clockOutHint: 'Leave empty if the day is still open',
        withBreak: 'With a break', submit: 'Record', done: 'Day recorded ({count} entries)',
      },
      closeDay: {
        title: 'Close the day',
        hint: "{worker}'s day on {date} has no end time.",
        startedAt: 'Started at {time}', submit: 'Close the day', done: 'Day closed',
      },
      validate: {
        title: 'Approve the day',
        hint: "{worker}'s day on {date}. Approving is your signature over these hours.",
        changedFrom: 'was {time}', correctionReason: 'Reason for the correction',
        notesHint: 'Optional, stays on the record',
        submit: 'Approve', submitWithChanges: 'Correct ({count}) and approve', done: 'Day approved',
      },
      correct: {
        title: 'Correct an entry',
        hint: 'The original is not deleted: it is voided and this one supersedes it.',
        submit: 'Correct', done: 'Entry corrected',
      },
      void: {
        title: 'Void the day',
        hint: "You are about to void {worker}'s day on {date}.",
        keeps: 'Entries are not deleted: they stay, marked as void, with your reason.',
        submit: 'Void', done: 'Day voided',
      },
      dayEntries: {
        title: 'Day detail', hint: '{worker} · {date}',
        byAdmin: 'Recorded by an administrator: {reason}', corrects: 'Supersedes a corrected entry', imported: 'Imported from {system}', recordedThere: 'recorded there on {at}',
      },
      my: {
        title: 'My time', subtitle: 'Your own record',
        workedToday: 'worked today', worked: 'Worked', today: 'Today',
        history: 'History', week: 'Week', month: 'Month', export: 'Download the month',
        noHistory: 'Nothing recorded yet',
        noHistoryHint: 'Once you clock in, your days will show up here.',
        dayDone: 'You have closed today.', reopen: 'Start another day',
        notStarted: 'You have not clocked in today',
        startedAt: 'Since {time}', pausedAt: 'On a break since {time}', finishedAt: 'You left at {time}',
      },
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Attendance",
        subtitle: "The working-time record the law requires, and why it does not count the hours of a job",
      },
      rules: {
        title: 'Labour agreement',
        agreement: 'Agreement: {name}',
        none: 'No agreement warnings',
        counted: 'Counted: {time}',
        paidBreak: 'Includes {time} of break paid by the agreement',
        hint: 'These are warnings: the record has not been touched and no clock-in was refused.',
        unknown: 'Warning from a client rule ({code})',
        warnings: {
          max_ordinary_hours: 'A {worked} day: over the {limit} of ordinary hours',
          min_rest: '{rest} of rest since the previous workday; the minimum is {required}',
          break_missing: '{continuous} in a row without a break of at least {minBreak}',
        },
      },
      search: { subtitle: 'See their days' },
      notifications: {
        categories: { dayLeftOpen: 'Days left open', dayRules: 'Labour agreement warnings' },
        dayRules: { title: 'Some days break the labour agreement' },
        dayLeftOpen: { title: 'Some days were left open' },
        yourDayLeftOpen: { title: 'Your day is still open', message: 'You have not clocked out today.' },
      },
    },

    modules: { attendance: 'Attendance' },
    assistant: {
      openers: {
        attendance: {
          'am-i-clocked-in': 'Am I clocked in right now?',
          'my-hours-this-week': 'How many hours have I worked this week?',
          'who-is-working-now': 'Who is working right now?',
        },
      },
    },
    permissions: {
      attendance: {
        clock: 'Clock and see your own time',
        read: "See everybody's days",
        manage: 'Correct, approve and void days',
        export: 'Export the working-time record',
        import: 'Import the working-time record of another system',
      },
    },
    settings: {
      ns: {
        attendance: {
          title: 'Attendance',
          subtitle: 'How long the record is kept, who hears about what, and the labour agreement rules',
          retentionYears: {
            label: 'Years the working-time record is kept',
            help: 'Spanish law requires four. Anything older is anonymised, never deleted.',
          },
          openDayAlertRoles: {
            label: 'Roles notified about days left open',
            help: 'They get one notification a night with the list.',
          },
          defaultCloseTime: { label: 'Default end time when closing a day by hand' },
          groups: { record: 'Record', alerts: 'Alerts', agreement: 'Labour agreement' },
          agreementName: {
            label: 'Labour agreement',
            help: 'The collective agreement these rules come from, so whoever reads a warning knows which text to check.',
          },
          agreementNotes: {
            label: 'Agreement notes',
            help: 'Anything about the agreement the rules below cannot express. Shown next to the warnings.',
          },
          maxOrdinaryDailyHours: {
            label: 'Maximum ordinary hours a day',
            help: 'Art. 34.3 ET sets nine unless the agreement says otherwise. Days recorded as overtime are not warned. 0 turns the check off.',
          },
          minRestHours: {
            label: 'Minimum rest between workdays',
            help: 'Art. 34.3 ET: twelve hours from the end of one workday to the start of the next. 0 turns the check off.',
          },
          breakAfterHours: {
            label: 'Continuous hours before a break is due',
            help: 'Art. 34.4 ET: a break when the continuous workday goes over six hours. 0 turns the check off.',
          },
          minBreakMinutes: {
            label: 'Minimum break',
            help: 'Art. 34.4 ET: at least fifteen minutes. A shorter stop does not count as the break.',
          },
          toleranceMinutes: {
            label: 'Clocking tolerance',
            help: 'Margin before any of these rules warns: with 5, nine hours and four minutes is not over nine.',
          },
          paidBreakMinutes: {
            label: 'Break counted as worked time',
            help: 'Minutes of break a day the agreement pays, such as the breakfast break. Shown as counted hours; the worked time does not change.',
          },
          ruleAlertRoles: {
            label: 'Roles notified about agreement breaches',
            help: 'Empty: the warnings are only shown on screen. With roles: one notification a night listing the days that broke a rule.',
          },
        },
      },
    },
    audit: {
      actions: {
        attendance: {
          registered_for_worker: 'Entry recorded on behalf of a worker',
          day_registered_for_worker: 'Day recorded on behalf of a worker',
          corrected: 'Entry corrected',
          day_validated: 'Day approved',
          day_voided: 'Day voided',
          exported: 'Working-time record exported',
          imported: 'Entry imported from another system',
        },
      },
    },
  },

  fr: {
    attendance: {
      // Las respuestas que contestan las reglas sin el modelo (intents.js).
      assistant: {
        query: {
          status: {
            in: 'Vous êtes pointé depuis {since} ; {hours} h {minutes} min aujourd’hui.',
            paused: 'Vous êtes en pause ; {hours} h {minutes} min aujourd’hui.',
            done: 'Vous avez pointé la sortie à {until} ; {hours} h {minutes} min aujourd’hui.',
            out: 'Vous n’avez pas encore pointé aujourd’hui.',
          },
          hours: {
            today: '{hours} h {minutes} min travaillées aujourd’hui.',
            week: '{hours} h {minutes} min travaillées cette semaine, sur {days} jours.',
            month: '{hours} h {minutes} min travaillées ce mois-ci, sur {days} jours.',
          },
        },
      },
      title: 'Pointage',
      dashboard: {
        myDay: 'Ma journée',
        myDayHelp: "Si vous êtes présent ou non, le temps travaillé aujourd'hui, et le bouton pour entrer, faire une pause ou sortir.",
        workingNow: 'Au travail maintenant',
        workingNowHelp: 'Combien de personnes ont leur journée ouverte en ce moment, pauses comprises.',
        onBreak: 'En pause',
        onBreakHelp: 'Parmi celles qui travaillent, combien ont une pause ouverte en ce moment.',
        who: 'Qui travaille',
        whoHelp: 'Qui a sa journée ouverte, et le temps travaillé aujourd\'hui. Étirez la carte et il en tient plus.',
        break: 'En pause',
      },
      subtitle: "Registre du temps de travail de l'équipe",
      inProgress: 'En cours',
      unknownWorker: 'Non attribué',
      exportDone: 'Exporté. Sceau {hash}',
      actions: {
        registerDay: 'Saisir une journée', export: 'Exporter', refresh: 'Actualiser',
        close: 'Clôturer', approve: 'Approuver', view: 'Voir', void: 'Annuler',
        correct: 'Corriger', cancel: 'Annuler',
        clock_in: 'Arrivée', clock_out: 'Départ', pause_start: 'Pause', pause_end: 'Reprendre',
      },
      filters: { title: 'Filtres', from: 'Du', to: 'Au', worker: 'Salarié', allWorkers: 'Tous' },
      kpi: { title: 'Résumé', workers: 'Salariés', approved: 'Approuvées', pending: 'En attente', open: 'Non clôturées', warnings: 'Avec alertes' },
      tabs: { days: 'Journées', entries: 'Pointages' },
      table: {
        rules: 'Convention',
        worker: 'Salarié', date: 'Date', in: 'Arrivée', out: 'Départ',
        worked: 'Effectives', break: 'Pause', entries: 'Nb', validation: 'Validation',
        type: 'Type', time: 'Moment', origin: 'Origine', byAdmin: 'Administratif', imported: 'Importé',
      },
      empty: {
        days: 'Aucune journée sur cette période',
        daysHint: 'Essaie d’élargir la plage de dates ou d’enlever le filtre salarié.',
        entries: 'Aucun pointage sur cette période',
        entriesHint: 'Chaque arrivée, pause et départ apparaît ici, une par ligne.',
      },
      status: { pending: 'En attente', approved: 'Approuvée', rejected: 'Rejetée' },
      types: { clock_in: 'Arrivée', clock_out: 'Départ', pause_start: 'Début de pause', pause_end: 'Fin de pause' },
      modality: { on_site: 'Sur site', remote: 'Télétravail' },
      hourKind: { ordinary: 'Ordinaires', overtime: 'Supplémentaires', complementary: 'Complémentaires' },
      state: { idle: 'Pas encore pointé', working: 'Au travail', paused: 'En pause', done: 'Journée clôturée' },
      done: { clock_in: 'Journée commencée', clock_out: 'Journée close', pause_start: 'Pause commencée', pause_end: 'Pause terminée' },
      fields: {
        reason: 'Motif', reasonPlaceholder: 'Pourquoi cette saisie',
        worker: 'Salarié', workerPlaceholder: 'Rechercher par nom',
        date: 'Date', clockIn: 'Arrivée', clockOut: 'Départ',
        pauseStart: 'Début de pause', pauseEnd: 'Fin de pause',
        modality: 'Modalité', hourKind: "Type d'heures", notes: 'Notes',
        type: 'Type', time: 'Moment',
      },
      reasons: {
        forgot: 'A oublié de pointer', deviceFailure: 'Panne du terminal',
        appFailure: "Panne de l'application", noDevice: 'Aucun terminal disponible',
        confirmedBySupervisor: 'Confirmé par le responsable', confirmedByWorker: 'Confirmé par le salarié',
      },
      errors: { workerRequired: 'Choisis un salarié', reasonRequired: 'Le motif est obligatoire' },
      registerDay: {
        title: 'Saisir une journée',
        hint: "Quand quelqu'un a travaillé sans pouvoir pointer. La journée est marquée administrative avec ton motif.",
        clockOutHint: 'Laisse vide si la journée est encore ouverte',
        withBreak: 'Avec pause', submit: 'Enregistrer', done: 'Journée enregistrée ({count} pointages)',
      },
      closeDay: {
        title: 'Clôturer la journée',
        hint: 'La journée de {worker} du {date} n’a pas d’heure de départ.',
        startedAt: 'Arrivée à {time}', submit: 'Clôturer', done: 'Journée clôturée',
      },
      validate: {
        title: 'Approuver la journée',
        hint: 'Journée de {worker} du {date}. Approuver vaut signature sur ces heures.',
        changedFrom: 'avant {time}', correctionReason: 'Motif de la correction',
        notesHint: 'Facultatif, conservé au registre',
        submit: 'Approuver', submitWithChanges: 'Corriger ({count}) et approuver', done: 'Journée approuvée',
      },
      correct: {
        title: 'Corriger un pointage',
        hint: "L'original n'est pas supprimé : il est annulé et celui-ci le remplace.",
        submit: 'Corriger', done: 'Pointage corrigé',
      },
      void: {
        title: 'Annuler la journée',
        hint: 'Tu vas annuler la journée de {worker} du {date}.',
        keeps: 'Les pointages ne sont pas supprimés : ils restent, marqués annulés, avec ton motif.',
        submit: 'Annuler', done: 'Journée annulée',
      },
      dayEntries: {
        title: 'Détail de la journée', hint: '{worker} · {date}',
        byAdmin: 'Saisi par un administrateur : {reason}', corrects: 'Remplace un pointage corrigé', imported: 'Importé de {system}', recordedThere: 'saisi là-bas le {at}',
      },
      my: {
        title: 'Mon temps', subtitle: 'Ton propre registre',
        workedToday: "travaillées aujourd'hui", worked: 'Travaillées', today: "Aujourd'hui",
        history: 'Historique', week: 'Semaine', month: 'Mois', export: 'Télécharger le mois',
        noHistory: 'Rien encore enregistré',
        noHistoryHint: 'Dès que tu pointes une arrivée, tes journées apparaîtront ici.',
        dayDone: "Tu as clôturé la journée d'aujourd'hui.", reopen: 'Commencer une autre journée',
        notStarted: "Tu n'as pas encore pointé aujourd'hui",
        startedAt: 'Depuis {time}', pausedAt: 'En pause depuis {time}', finishedAt: 'Tu es parti à {time}',
      },
      // La explicacion entera del modulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aqui solo van
      // las dos frases que el indice de la ayuda necesita antes de abrirlo.
      help: {
        title: "Pointage",
        subtitle: "Le registre du temps de travail qu’exige la loi, et pourquoi il ne compte pas les heures d’un chantier",
      },
      rules: {
        title: 'Convention collective',
        agreement: 'Convention : {name}',
        none: 'Aucune alerte de la convention',
        counted: 'Comptabilisées : {time}',
        paidBreak: 'Dont {time} de pause payée par la convention',
        hint: "Ce sont des alertes : le registre n'a pas été modifié et aucun pointage n'a été refusé.",
        unknown: "Alerte d'une règle du client ({code})",
        warnings: {
          max_ordinary_hours: 'Journée de {worked} : dépasse les {limit} ordinaires',
          min_rest: 'Repos de {rest} depuis la journée précédente ; le minimum est de {required}',
          break_missing: '{continuous} d’affilée sans une pause d’au moins {minBreak}',
        },
      },
      search: { subtitle: 'Voir ses journées' },
      notifications: {
        categories: { dayLeftOpen: 'Journées non clôturées', dayRules: 'Alertes de la convention' },
        dayRules: { title: 'Des journées ne respectent pas la convention' },
        dayLeftOpen: { title: 'Des journées sont restées ouvertes' },
        yourDayLeftOpen: { title: 'Ta journée est encore ouverte', message: "Tu n'as pas pointé ton départ." },
      },
    },

    modules: { attendance: 'Pointage' },
    assistant: {
      openers: {
        attendance: {
          'am-i-clocked-in': 'Suis-je pointé en ce moment ?',
          'my-hours-this-week': "Combien d'heures ai-je faites cette semaine ?",
          'who-is-working-now': 'Qui travaille en ce moment ?',
        },
      },
    },
    permissions: {
      attendance: {
        clock: 'Pointer et voir son propre temps',
        read: 'Voir les journées de tout le monde',
        manage: 'Corriger, approuver et annuler des journées',
        export: 'Exporter le registre du temps de travail',
        import: "Importer le registre du temps de travail d'un autre système",
      },
    },
    settings: {
      ns: {
        attendance: {
          title: 'Pointage',
          subtitle: 'Conservation du registre, alertes et règles de la convention collective',
          retentionYears: {
            label: 'Années de conservation du registre',
            help: "La loi en exige quatre. Ce qui est plus ancien est anonymisé, jamais supprimé.",
          },
          openDayAlertRoles: {
            label: 'Rôles alertés des journées non clôturées',
            help: 'Ils reçoivent une alerte chaque nuit avec la liste.',
          },
          defaultCloseTime: { label: 'Heure de départ par défaut pour clôturer une journée à la main' },
          groups: { record: 'Registre', alerts: 'Alertes', agreement: 'Convention collective' },
          agreementName: {
            label: 'Convention collective',
            help: "La convention d'où viennent ces règles, pour que qui lit une alerte sache quel texte consulter.",
          },
          agreementNotes: {
            label: 'Notes sur la convention',
            help: "Ce que les règles ci-dessous ne peuvent pas exprimer. Affiché à côté des alertes.",
          },
          maxOrdinaryDailyHours: {
            label: 'Maximum d’heures ordinaires par jour',
            help: "L'art. 34.3 ET en fixe neuf sauf disposition contraire de la convention. Les journées saisies en heures supplémentaires ne déclenchent pas d'alerte. 0 désactive le contrôle.",
          },
          minRestHours: {
            label: 'Repos minimum entre deux journées',
            help: "Art. 34.3 ET : douze heures entre la fin d'une journée et le début de la suivante. 0 désactive le contrôle.",
          },
          breakAfterHours: {
            label: 'Heures d’affilée avant la pause obligatoire',
            help: "Art. 34.4 ET : une pause quand la journée continue dépasse six heures. 0 désactive le contrôle.",
          },
          minBreakMinutes: {
            label: 'Pause minimale',
            help: "Art. 34.4 ET : au moins quinze minutes. Un arrêt plus court ne compte pas comme la pause.",
          },
          toleranceMinutes: {
            label: 'Tolérance de pointage',
            help: "Marge avant qu'une de ces règles n'alerte : avec 5, neuf heures et quatre minutes ne dépassent pas neuf.",
          },
          paidBreakMinutes: {
            label: 'Pause comptée comme travail',
            help: "Minutes de pause par jour payées par la convention, comme la pause casse-croûte. Affichées comme heures comptabilisées ; le temps travaillé ne change pas.",
          },
          ruleAlertRoles: {
            label: 'Rôles alertés des manquements à la convention',
            help: "Vide : les alertes ne sont visibles qu'à l'écran. Avec des rôles : une notification chaque nuit listant les journées concernées.",
          },
        },
      },
    },
    audit: {
      actions: {
        attendance: {
          registered_for_worker: "Pointage saisi au nom d'un salarié",
          day_registered_for_worker: "Journée saisie au nom d'un salarié",
          corrected: 'Pointage corrigé',
          day_validated: 'Journée approuvée',
          day_voided: 'Journée annulée',
          exported: 'Registre du temps de travail exporté',
          imported: "Pointage importé d'un autre système",
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(baseMessages, 'attendance')))

// Las tarjetas del asistente (attendance.assistant.clock.*), en su propio
// fichero: son un contrato con backend/assistant.js. Se mezclan con lo que ya
// cuelga de attendance.assistant (las respuestas de las reglas), sin pisarlo.
import assistantMessages from './assistantMessages.js'

for (const [locale, block] of Object.entries(assistantMessages)) {
  messages[locale].attendance.assistant = { ...messages[locale].attendance.assistant, ...block }
}

export default { routes, navigation, messages }
